import React, { useMemo } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import { Link } from "react-router-dom";

import PageHeader from "../../app/layout/PageHeader";
import { useAccess } from "../../context/AccessContext";
import {
  REPORT_CATEGORIES,
  REPORTS,
  ReportDefinition,
  ReportIcon,
} from "./reportRegistry";
import "./ReportsPage.css";

const reportIcons: Record<ReportIcon, React.ReactNode> = {
  "session-summary": <SummarizeOutlinedIcon />,
  "contact-issues": <ContactPhoneOutlinedIcon />,
  "archived-parties": <ArchiveOutlinedIcon />,
};

export default function ReportsPage() {
  const { hasFeature } = useAccess();

  const availableReports = useMemo(
    () => REPORTS.filter((report) => !report.feature || hasFeature(report.feature)),
    [hasFeature],
  );

  const reportsByCategory = useMemo(
    () =>
      REPORT_CATEGORIES.map((category) => ({
        category,
        reports: availableReports.filter((report) => report.category === category),
      })).filter((group) => group.reports.length > 0),
    [availableReports],
  );

  return (
    <div className="page-container reports-page">
      <PageHeader
        title="Report Centre"
        subtitle="View operational reports and information that needs attention."
      />

      <div className="reports-groups">
        {reportsByCategory.map(({ category, reports }) => (
          <section className="reports-group" key={category}>
            <h2>{category}</h2>
            <div className="reports-list">
              {reports.map((report) => (
                <ReportLink report={report} key={report.id} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function ReportLink({ report }: { report: ReportDefinition }) {
  return (
    <Link className="report-link" to={report.path}>
      <span className="report-link-icon" aria-hidden="true">
        {reportIcons[report.icon]}
      </span>
      <span className="report-link-copy">
        <strong>{report.title}</strong>
        <span>{report.description}</span>
      </span>
      <ChevronRightIcon className="report-link-chevron" aria-hidden="true" />
    </Link>
  );
}
